import React from 'react';
import Link from '../components/LocalizedLink';

import { ArrowRight, MessageCircle, FileText, Code, Rocket } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/button';
import { FadeInUp, StaggerContainer, StaggerItem } from '../components/AnimatedSection';

const ComeFunzionaPage = () => {
  const { t } = useTranslation();
  const steps = [
    {
      num: "1",
      icon: MessageCircle,
      title: t('come_funziona.steps.1_title'),
      desc: t('come_funziona.steps.1_desc'),
      details: [t('come_funziona.steps.1_d1'), t('come_funziona.steps.1_d2'), t('come_funziona.steps.1_d3')]
    },
    {
      num: "2",
      icon: FileText,
      title: t('come_funziona.steps.2_title'),
      desc: t('come_funziona.steps.2_desc'),
      details: [t('come_funziona.steps.2_d1'), t('come_funziona.steps.2_d2'), t('come_funziona.steps.2_d3')]
    },
    {
      num: "3",
      icon: Code,
      title: t('come_funziona.steps.3_title'),
      desc: t('come_funziona.steps.3_desc'),
      details: [t('come_funziona.steps.3_d1'), t('come_funziona.steps.3_d2'), t('come_funziona.steps.3_d3')]
    },
    {
      num: "4",
      icon: Rocket,
      title: t('come_funziona.steps.4_title'),
      desc: t('come_funziona.steps.4_desc'),
      details: [t('come_funziona.steps.4_d1'), t('come_funziona.steps.4_d2'), t('come_funziona.steps.4_d3')]
    }
  ];

  return (
    <div data-testid="come-funziona-page">
      {/* Hero */}
      <section className="py-16 md:py-24 bg-[var(--color-primary)] relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-20 right-0 w-[400px] h-[400px] bg-white/5 rounded-full" />
          <div className="absolute bottom-0 -left-20 w-[300px] h-[300px] bg-white/5 rounded-full" />
          <div className="absolute top-20 right-20 w-24 h-24 opacity-20">
            <div className="grid grid-cols-3 gap-2">
              {[...Array(9)].map((_, i) => (
                <div key={i} className="w-2 h-2 rounded-full bg-white" />
              ))}
            </div>
          </div>
        </div>
        
        <div className="max-w-4xl mx-auto px-6 md:px-12 relative z-10">
          <FadeInUp>
            <span className="text-white/80 uppercase text-sm font-semibold tracking-widest mb-4 inline-block">{t('come_funziona.hero_tag')}</span>
          </FadeInUp>
          <FadeInUp delay={0.1}>
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-white mb-6">
              {t('come_funziona.hero_title')}
            </h1>
          </FadeInUp>
          <FadeInUp delay={0.2}>
            <p className="text-xl md:text-2xl text-white/80 max-w-2xl">
              {t('come_funziona.hero_desc_1')}
              <strong className="text-white"> {t('come_funziona.hero_desc_2')}</strong>
            </p>
          </FadeInUp>
        </div>
      </section>

      {/* Steps */}
      <section className="py-16 md:py-24 bg-[var(--color-surface)] relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/4 -right-10 w-40 h-40 bg-[var(--color-primary)]/5 rounded-full blur-2xl" />
          <div className="absolute bottom-1/4 -left-10 w-60 h-60 bg-[var(--color-accent)]/5 rounded-full blur-3xl" />
          <div className="absolute left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-transparent via-[var(--color-border)] to-transparent opacity-50 hidden md:block" />
        </div>
        
        <div className="max-w-4xl mx-auto px-6 md:px-12 relative z-10">
          <StaggerContainer className="space-y-0" staggerDelay={0.15}>
            {steps.map((step, index) => (
              <StaggerItem key={step.num}>
                <div 
                  className={`flex flex-col md:flex-row gap-6 md:gap-12 py-12 ${
                    index !== steps.length - 1 ? 'border-b border-[var(--color-border)]' : ''
                  }`}
                  data-testid={`step-detail-${step.num}`}
                >
                  <div className="flex-shrink-0">
                    <div className="flex items-center gap-4">
                      <div className="step-number">{step.num}</div>
                      <div className="w-12 h-12 rounded-xl bg-[var(--color-primary)]/10 flex items-center justify-center">
                        <step.icon className="w-6 h-6 text-[var(--color-primary)]" />
                      </div>
                    </div>
                  </div>

                  <div className="flex-1">
                    <h2 className="text-2xl font-bold text-[var(--color-text)] mb-3">
                      {step.title}
                    </h2>
                    <p className="text-lg text-[var(--color-text-muted)] mb-5 leading-relaxed">
                      {step.desc}
                    </p>
                    <ul className="flex flex-wrap gap-3">
                      {step.details.map((detail, idx) => (
                        <li 
                          key={idx}
                          className="text-sm bg-[var(--color-bg)] text-[var(--color-text-muted)] px-4 py-2 rounded-full"
                        >
                          {detail}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-24 bg-[var(--color-accent)] relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-20 -left-20 w-60 h-60 bg-white/5 rounded-full" />
          <div className="absolute -bottom-10 -right-10 w-80 h-80 bg-white/5 rounded-full" />
        </div>
        
        <div className="max-w-3xl mx-auto px-6 md:px-12 text-center relative z-10">
          <FadeInUp>
            <h2 className="text-3xl font-bold text-white mb-4">
              {t('come_funziona.cta_title')}
            </h2>
          </FadeInUp>
          <FadeInUp delay={0.1}>
            <p className="text-lg md:text-xl text-white/80 mb-8">
              {t('come_funziona.cta_desc')}
            </p>
          </FadeInUp>
          <FadeInUp delay={0.2}>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/tariffe">
                <Button data-testid="cta-btn" className="bg-white text-[var(--color-accent)] hover:bg-white/90 text-lg px-8 py-6 h-auto rounded-full font-semibold shadow-lg">
                  {t('common.scopri_prezzi')}
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <a href="https://wa.me/393534992310" target="_blank" rel="noopener noreferrer">
                <Button variant="outline" className="text-lg px-8 py-6 h-auto rounded-full border-2 border-white text-white hover:bg-white/10">
                  <MessageCircle className="mr-2 w-5 h-5" />
                  {t('common.scrivici')}
                </Button>
              </a>
            </div>
          </FadeInUp>
        </div>
      </section>
    </div>
  );
};

export default ComeFunzionaPage;
